import { Controller } from "@hotwired/stimulus"

// Pull down from the top of a feed on a phone, and it is brought up to date.
// A small brass ring comes down with the finger, closes as the pull nears
// enough, and turns while the page is fetched again. Touch only; the native
// apps have their own pull, so this is not connected there (see the views).
export default class extends Controller {
  static values = {
    threshold: { type: Number, default: 72 },
    resistance: { type: Number, default: 0.5 }
  }

  connect() {
    if (!window.matchMedia("(pointer: coarse)").matches) return
    this.mark = this.draw()
    this.pulling = false
    this.onStart = (e) => this.start(e)
    this.onMove = (e) => this.move(e)
    this.onEnd = () => this.end()
    this.element.addEventListener("touchstart", this.onStart, { passive: true })
    this.element.addEventListener("touchmove", this.onMove, { passive: false })
    this.element.addEventListener("touchend", this.onEnd)
    this.element.addEventListener("touchcancel", this.onEnd)
    // The browser's own pull-to-reload would fire underneath ours.
    this.wasOverscroll = document.documentElement.style.overscrollBehaviorY
    document.documentElement.style.overscrollBehaviorY = "contain"
  }

  disconnect() {
    if (!this.mark) return
    this.element.removeEventListener("touchstart", this.onStart)
    this.element.removeEventListener("touchmove", this.onMove)
    this.element.removeEventListener("touchend", this.onEnd)
    this.element.removeEventListener("touchcancel", this.onEnd)
    document.documentElement.style.overscrollBehaviorY = this.wasOverscroll
    this.mark.remove()
  }

  start(event) {
    if (this.refreshing || window.scrollY > 0 || event.touches.length !== 1) return
    this.startY = event.touches[0].clientY
    this.pulling = true
    this.distance = 0
  }

  move(event) {
    if (!this.pulling) return
    const dy = event.touches[0].clientY - this.startY
    if (dy <= 0 || window.scrollY > 0) { this.distance = 0; return this.show(0) }

    // Past the first few pixels it is a pull, not a scroll; the page holds.
    if (dy > 8 && event.cancelable) event.preventDefault()
    this.distance = Math.min(dy * this.resistanceValue, this.thresholdValue * 1.5)
    this.show(this.distance)
  }

  end() {
    if (!this.pulling) return
    this.pulling = false
    if (this.distance >= this.thresholdValue) this.refresh()
    else this.show(0)
  }

  show(distance) {
    const ready = distance >= this.thresholdValue
    this.mark.style.setProperty("--pull", `${distance}px`)
    this.mark.style.setProperty("--reach", Math.min(distance / this.thresholdValue, 1).toFixed(3))
    this.mark.classList.toggle("pull-ready", ready)
    this.mark.classList.toggle("pull-shown", distance > 0)
  }

  // The page is fetched again in place; the ring turns until it lands. When
  // the new page arrives this controller is gone with the old one, mark and all.
  refresh() {
    this.refreshing = true
    this.mark.classList.add("pull-refreshing")
    this.show(this.thresholdValue * 0.8)
    if (navigator.vibrate) navigator.vibrate(8)
    window.Turbo.visit(window.location.href, { action: "replace" })
    // Should the visit not land (offline), let the page go after a while.
    setTimeout(() => { if (this.mark?.isConnected) { this.refreshing = false; this.mark.classList.remove("pull-refreshing"); this.show(0) } }, 8000)
  }

  draw() {
    const mark = document.createElement("div")
    mark.className = "pull"
    mark.setAttribute("aria-hidden", "true")
    // It comes down from beneath the masthead, where there is one.
    const masthead = document.querySelector("header.sticky")
    if (masthead) mark.style.setProperty("--from", `${Math.round(masthead.getBoundingClientRect().bottom)}px`)
    mark.innerHTML = `<svg viewBox="0 0 24 24" class="pull-ring"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-opacity="0.2" stroke-width="1.5"/><circle class="pull-arc" cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" pathLength="100" transform="rotate(-90 12 12)"/></svg>`
    document.body.appendChild(mark)
    return mark
  }
}
